import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
// eslint-disable-next-line no-unused-vars
import { EventsGrid } from "./EventsGrid";
const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const TeamEventsTab = ({ teamId }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [token] = useState(localStorage.getItem("token"));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const EVENTS_PER_PAGE = 6; // Adjust this value as needed

  useEffect(() => {
    const fetchEvents = async () => {
      if (!teamId) return;

      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/event/get-all-events-by-team-id?teamId=${teamId}&page=${page}&limit=${EVENTS_PER_PAGE}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch events");
        }

        const data = await response.json();

        // Update state with the response data
        setEvents(data.events || []);
        setTotalPages(data.totalPages || 1);
        setTotalEvents(data.total || 0);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching events:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [teamId, page, token]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // eslint-disable-next-line no-unused-vars
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center gap-4 mt-6">
        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          className={`px-4 py-2 rounded-md ${
            page === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Previous
        </button>

        <span className="text-gray-600">
          Page {page} of {totalPages}
        </span>

        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          className={`px-4 py-2 rounded-md ${
            page === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div role="tabpanel" id="events-panel" aria-labelledby="events-tab" className="mt-8 max-w-7xl mx-auto px-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Team Events</h2>
          {!isLoading && (
            <p className="text-base text-gray-600 mt-2">
              Showing {events.length} of {totalEvents} {totalEvents === 1 ? 'event' : 'events'}
            </p>
          )}
        </div>

        <button
          onClick={() => navigate(`/organization/events/create/team/${teamId}`)}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#F15213] to-[#ff6b3d] text-white rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Create Event
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-xl mb-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600 underline"
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#F15213] border-t-transparent shadow-md"></div>
        </div>
      ) : events.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Link
                to={`/organization/events/${event._id}`}
                key={event._id}
                className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#F15213]/20"
              >
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#F15213] transition-colors line-clamp-1">
                      {event.name}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        event.eventStatus === "Upcoming"
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : event.eventStatus === "In Progress"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-gray-50 text-gray-700 border border-gray-200"
                      }`}
                    >
                      {event.eventStatus}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 line-clamp-2 text-sm leading-relaxed">
                    {event.description}
                  </p>
                  
                  <div className="flex items-center text-sm text-gray-500 pt-2 border-t border-gray-100">
                    <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {new Date(event.startDate).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-10">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className={`px-4 py-2.5 rounded-lg flex items-center font-medium transition-all duration-200 ${
                  page === 1
                    ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 hover:text-[#F15213] border border-gray-200 hover:border-[#F15213]"
                }`}
              >
                <svg className="h-5 w-5 mr-1" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              <span className="text-gray-700 font-medium px-4 py-2.5 bg-gray-50 rounded-lg">
                {page} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`px-4 py-2.5 rounded-lg flex items-center font-medium transition-all duration-200 ${
                  page === totalPages
                    ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 hover:text-[#F15213] border border-gray-200 hover:border-[#F15213]"
                }`}
              >
                Next
                <svg className="h-5 w-5 ml-1" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20 bg-gray-50 rounded-xl border border-gray-200">
          <div className="max-w-sm mx-auto">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No events found</h3>
            <p className="mt-2 text-gray-500">Get started by creating a new event for this team.</p>
            <button
              onClick={() => navigate(`/organization/events/create/team/${teamId}`)}
              className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-[#F15213] bg-white border border-[#F15213] rounded-lg hover:bg-[#F15213] hover:text-white transition-colors duration-200"
            >
              Create your first event
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamEventsTab;
